import React, { useState, useEffect } from "react";
import { useNavigate, Link } from "react-router-dom";
import Layout from "../../../components/Layout";
import axios from "axios";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL ||
  "http://localhost/securecontrol-backend";

// Palette de couleurs SecureControl
const COLORS = {
  primary: '#0B3C5D',     // Bleu Sécurité
  secondary: '#1E88C8',   // Bleu Tech
  accent: '#4FC3F7',      // Bleu Clair
  darkText: '#2E2E2E',    // Gris Foncé
  lightBg: '#F5F7FA',     // Gris Clair
  white: '#FFFFFF',
  danger: '#f44336',
  success: '#4caf50',
  warning: '#ff9800',
  info: '#2196f3',
  successLight: '#e8f5e9',
  dangerLight: '#ffebee',
  warningLight: '#fff3e0',
  infoLight: '#e3f2fd'
};

const TypeIncidentsList = () => {
  const navigate = useNavigate();
  const [allTypes, setAllTypes] = useState([]);
  const [filteredTypes, setFilteredTypes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [serverError, setServerError] = useState("");
  const [infoMsg, setInfoMsg] = useState("");
  const [userInfo, setUserInfo] = useState(null);
  
  // État pour le filtre recherche uniquement
  const [filterSearch, setFilterSearch] = useState("");
  
  // États pour la pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  // Récupérer les infos utilisateur
  useEffect(() => {
    const storedUser = localStorage.getItem("user");
    if (storedUser) {
      try {
        const userData = JSON.parse(storedUser);
        const normalizedData = {
          ...userData,
          profile: userData.profile || userData.poste || 0,
          societe_id: userData.societe_id || 0
        };
        setUserInfo(normalizedData);
      } catch (error) {
        navigate("/login");
      }
    } else {
      navigate("/login");
    }
  }, [navigate]);

  // Charger la liste des types d'incidents une seule fois au début
  const fetchAllTypes = async () => {
    try {
      setLoading(true);
      setServerError("");
      setInfoMsg("");
      
      const token = localStorage.getItem("access_token");
      if (!token) {
        setServerError("Session expirée. Veuillez vous reconnecter.");
        setTimeout(() => navigate("/login"), 2000);
        setLoading(false);
        return;
      }

      const url = `${API_BASE_URL}/incidents/type_incident/liste_type_incidents.php`;

      const response = await axios.get(url, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Accept': 'application/json'
        },
        timeout: 10000
      });

      if (response.data.success) {
        const types = response.data.types_incident || [];
        setAllTypes(types);
        setFilteredTypes(types);
        
        if (types.length === 0) {
          setInfoMsg("Aucun type d'incident trouvé.");
        }
      } else {
        setServerError(response.data.message || "Erreur lors du chargement des données");
        setAllTypes([]);
        setFilteredTypes([]);
      }
    } catch (err) {
      handleFetchError(err);
    } finally {
      setLoading(false);
    }
  };

  const handleFetchError = (err) => {
    console.error("Erreur chargement types d'incidents :", err);
    if (err.response?.status === 401) {
      setServerError("Session expirée. Veuillez vous reconnecter.");
      localStorage.removeItem("access_token");
      setTimeout(() => navigate("/login"), 2000);
    } else if (err.response?.status === 500) {
      setServerError("Erreur serveur. Veuillez contacter l'administrateur.");
    } else if (err.request) {
      setServerError("Serveur inaccessible. Vérifiez votre connexion.");
    } else {
      setServerError("Erreur technique lors du chargement des types d'incidents.");
    }
    setAllTypes([]);
    setFilteredTypes([]);
  };

  // Charger tous les types une seule fois au début
  useEffect(() => {
    fetchAllTypes();
  }, []);

  // Appliquer les filtres
  useEffect(() => {
    if (allTypes.length > 0) {
      let filtered = [...allTypes];

      // Filtre de recherche texte seulement
      if (filterSearch.trim()) {
        const searchTerm = filterSearch.toLowerCase().trim();
        filtered = filtered.filter(type => {
          // Recherche dans le nom du type
          if (type.type && type.type.toLowerCase().includes(searchTerm)) {
            return true;
          }
          
          // Recherche dans la description
          if (type.description && type.description.toLowerCase().includes(searchTerm)) {
            return true;
          }
          
          // Recherche dans l'ID
          if (type.id && type.id.toString().includes(searchTerm)) {
            return true;
          }
          
          return false;
        });
      }

      setFilteredTypes(filtered);
      setCurrentPage(1);
      
      if (filtered.length === 0 && filterSearch.trim()) {
        setInfoMsg(`Aucun type d'incident trouvé pour "${filterSearch}"`);
      } else if (filtered.length === 0) {
        setInfoMsg("Aucun type d'incident ne correspond aux critères de recherche.");
      } else {
        setInfoMsg("");
      }
    }
  }, [allTypes, filterSearch]);

  // Fonction pour formater le badge d'incidents
  const getIncidentBadge = (count) => {
    if (count === 0 || !count) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#F5F7FA',
          color: COLORS.darkText,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 5) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: COLORS.infoLight,
          color: COLORS.info,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 10) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#fff3e0',
          color: '#f57c00',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else if (count < 20) {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#fff3e0',
          color: '#ef6c00',
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    } else {
      return (
        <span className="badge fw-semibold" style={{ 
          backgroundColor: '#ffebee',
          color: COLORS.danger,
          padding: '8px 16px',
          borderRadius: '6px',
          fontSize: '0.85rem'
        }}>
          {count}
        </span>
      );
    }
  };

  // Pagination
  const indexOfLastItem = currentPage * itemsPerPage;
  const indexOfFirstItem = indexOfLastItem - itemsPerPage;
  const currentItems = filteredTypes.slice(indexOfFirstItem, indexOfLastItem);
  const totalPages = Math.ceil(filteredTypes.length / itemsPerPage);

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleItemsPerPageChange = (e) => {
    setItemsPerPage(parseInt(e.target.value));
    setCurrentPage(1);
  };

  const resetFilters = () => {
    setFilterSearch("");
  };

  const hasActiveFilters = () => {
    return filterSearch.trim();
  };

  return (
    <Layout>
      {/* Titre de page */}
      <div className="pagetitle">
        <h1 style={{ color: COLORS.primary, fontWeight: '600' }}>Types d'incidents</h1>
        <nav>
          <ol className="breadcrumb">
            <li className="breadcrumb-item">
              <Link to="/dashboard" style={{ color: COLORS.primary }}>Tableau de bord</Link>
            </li>
            <li className="breadcrumb-item">Incidents</li>
            <li className="breadcrumb-item active" style={{ color: COLORS.secondary }}>Types d'incidents</li>
          </ol>
        </nav>
      </div>

      {/* Contenu */}
      <section className="section">
        <div className="card" style={{ 
          borderRadius: '12px',
          boxShadow: '0 4px 12px rgba(0,0,0,0.08)',
          border: 'none'
        }}>
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-4">
              <div>
                <h5 className="card-title mb-1" style={{ 
                  color: COLORS.primary,
                  borderBottom: `2px solid ${COLORS.lightBg}`,
                  paddingBottom: '16px',
                  width: '100%'
                }}>
                  <div className="d-flex align-items-center">
                    <div className="rounded-circle d-inline-flex align-items-center justify-content-center me-3" style={{ 
                      width: '36px',
                      height: '36px',
                      backgroundColor: '#E3F2FD',
                      color: COLORS.primary
                    }}>
                      <i className="bi bi-clipboard"></i>
                    </div>
                    Liste des types d'incidents
                  </div>
                </h5>
                 
              </div>
              
              <div className="d-flex gap-2">
                {hasActiveFilters() && (
                  <button
                    className="btn fw-semibold"
                    onClick={resetFilters}
                    title="Réinitialiser la recherche"
                    style={{
                      color: COLORS.primary,
                      borderColor: COLORS.primary,
                      backgroundColor: 'transparent',
                      borderRadius: '8px',
                      padding: '10px 20px',
                      transition: 'all 0.3s ease'
                    }}
                    onMouseEnter={(e) => {
                      e.target.style.backgroundColor = '#E3F2FD';
                      e.target.style.borderColor = COLORS.secondary;
                    }}
                    onMouseLeave={(e) => {
                      e.target.style.backgroundColor = 'transparent';
                      e.target.style.borderColor = COLORS.primary;
                    }}
                  >
                    <i className="bi bi-arrow-clockwise me-2"></i>
                    Réinitialiser
                  </button>
                )}
                <button
                  className="btn fw-semibold"
                  onClick={() => navigate("/create")}
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.borderColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(-2px)';
                    e.target.style.boxShadow = '0 6px 12px rgba(11, 60, 93, 0.2)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = 'none';
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i> 
                  Nouveau type
                </button>
              </div>
            </div>

            {serverError && (
              <div className="alert alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#ffebee',
                borderColor: COLORS.danger,
                color: '#c62828',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.danger}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-exclamation-triangle me-2"></i>
                  <div className="flex-grow-1">{serverError}</div>
                  <button 
                    type="button" 
                    className="btn-close" 
                    onClick={() => setServerError("")}
                  ></button>
                </div>
              </div>
            )}
            
            {infoMsg && !loading && (
              <div className="alert alert-success alert-dismissible fade show mb-4" style={{ 
                backgroundColor: '#e8f5e9',
                borderColor: COLORS.success,
                color: '#2e7d32',
                borderRadius: '8px',
                borderLeft: `4px solid ${COLORS.success}`
              }}>
                <div className="d-flex align-items-center">
                  <i className="bi bi-info-circle me-2"></i>
                  <div className="flex-grow-1">
                    {infoMsg.split('\n').map((line, i) => (
                      <div key={i}>{line}</div>
                    ))}
                  </div>
                  <button type="button" className="btn-close" onClick={() => setInfoMsg("")}></button>
                </div>
              </div>
            )}

            {/* Filtre recherche */}
            <div className="row mb-4">
              <div className="col-12">
                <div className="card" style={{ 
                  backgroundColor: '#E3F2FD',
                  border: 'none',
                  borderRadius: '8px'
                }}>
                  <div className="card-body py-3">
                    <div className="row g-3">
                      <div className="col-md-6">
                        <div className="input-group">
                          <span className="input-group-text" style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderColor: '#e0e0e0',
                            color: COLORS.secondary
                          }}>
                            <i className="bi bi-search"></i>
                          </span>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Rechercher un type d'incident..."
                            value={filterSearch}
                            onChange={(e) => setFilterSearch(e.target.value)}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText
                            }}
                          />
                          {filterSearch && (
                            <button
                              type="button"
                              className="btn"
                              onClick={() => setFilterSearch("")}
                              title="Effacer la recherche"
                              style={{
                                backgroundColor: COLORS.lightBg,
                                borderColor: '#e0e0e0',
                                color: COLORS.darkText,
                                transition: 'all 0.3s ease'
                              }}
                              onMouseEnter={(e) => {
                                e.target.style.backgroundColor = '#e0e0e0';
                              }}
                              onMouseLeave={(e) => {
                                e.target.style.backgroundColor = COLORS.lightBg;
                              }}
                            >
                              <i className="bi bi-x"></i>
                            </button>
                          )}
                        </div>
                      </div>
                    </div>

                    {/* Résumé des filtres */}
                    <div className="mt-3 d-flex justify-content-between align-items-center">
                      <div className="text-muted small" style={{ color: COLORS.darkText }}>
                        <i className="bi bi-info-circle me-1"></i>
                        <span className="badge fw-semibold me-2" style={{ 
                          backgroundColor: COLORS.lightBg,
                          color: COLORS.primary,
                          padding: '4px 12px',
                          borderRadius: '6px'
                        }}>
                          {filteredTypes.length} type(s)
                          {filteredTypes.length !== allTypes.length && 
                            ` sur ${allTypes.length} au total`}
                        </span>
                        
                        {hasActiveFilters() && (
                          <span>
                            <i className="bi bi-funnel me-1"></i>
                            Recherche: "{filterSearch}"
                          </span>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border" role="status" style={{ color: COLORS.primary }}>
                  <span className="visually-hidden">Chargement...</span>
                </div>
                <p className="mt-2" style={{ color: COLORS.darkText }}>Chargement des types d'incidents...</p>
              </div>
            ) : allTypes.length === 0 ? (
              <div className="text-center py-5">
                <div className="mb-4">
                  <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                    width: '80px',
                    height: '80px',
                    backgroundColor: '#F5F7FA',
                    color: '#B0BEC5'
                  }}>
                    <i className="bi bi-clipboard fs-1"></i>
                  </div>
                </div>
                <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun type d'incident trouvé</h6>
                <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                  Aucun type d'incident n'a été enregistré pour le moment.
                </p>
                <button
                  onClick={() => navigate("/create")}
                  className="btn fw-semibold mt-3"
                  style={{
                    color: COLORS.white,
                    backgroundColor: COLORS.primary,
                    borderColor: COLORS.primary,
                    borderRadius: '8px',
                    padding: '10px 24px',
                    transition: 'all 0.3s ease'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = COLORS.secondary;
                    e.target.style.borderColor = COLORS.secondary;
                    e.target.style.transform = 'translateY(-2px)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = COLORS.primary;
                    e.target.style.borderColor = COLORS.primary;
                    e.target.style.transform = 'translateY(0)';
                  }}
                >
                  <i className="bi bi-plus-lg me-2"></i> 
                  Ajouter un type
                </button>
              </div>
            ) : (
              <>
                {/* Affichage des résultats filtrés */}
                {filteredTypes.length === 0 && filterSearch.trim() ? (
                  <div className="text-center py-5">
                    <div className="mb-4">
                      <div className="rounded-circle d-flex align-items-center justify-content-center mx-auto" style={{ 
                        width: '80px',
                        height: '80px',
                        backgroundColor: '#F5F7FA',
                        color: '#B0BEC5'
                      }}>
                        <i className="bi bi-search fs-1"></i>
                      </div>
                    </div>
                    <h6 className="mb-3" style={{ color: COLORS.primary }}>Aucun résultat</h6>
                    <p className="text-muted" style={{ color: COLORS.darkText, maxWidth: '500px', margin: '0 auto' }}>
                      Aucun type d'incident ne correspond à la recherche "{filterSearch}".
                    </p>
                    <button
                      className="btn fw-semibold mt-3"
                      onClick={resetFilters}
                      style={{
                        color: COLORS.primary,
                        borderColor: COLORS.primary,
                        backgroundColor: 'transparent',
                        borderRadius: '8px',
                        padding: '10px 24px',
                        transition: 'all 0.3s ease'
                      }}
                      onMouseEnter={(e) => {
                        e.target.style.backgroundColor = COLORS.primary;
                        e.target.style.color = COLORS.white;
                      }}
                      onMouseLeave={(e) => {
                        e.target.style.backgroundColor = 'transparent';
                        e.target.style.color = COLORS.primary;
                      }}
                    >
                      <i className="bi bi-x-lg me-2"></i> 
                      Effacer la recherche
                    </button>
                  </div>
                ) : (
                  <>
                    <div className="table-responsive">
                      <table className="table table-hover mb-0">
                        <thead>
                          <tr style={{ 
                            backgroundColor: COLORS.lightBg,
                            borderBottom: `2px solid ${COLORS.accent}`
                          }}>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px' }}>Type d'incident</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Incidents</th>
                            <th style={{ color: COLORS.primary, fontWeight: '600', padding: '16px', textAlign: 'center' }}>Actions</th>
                          </tr>
                        </thead>
                        <tbody>
                          {currentItems.map((type, index) => {
                            const incidentCount = type.nombre_incidents || type.nb_incidents || 0;
                            
                            return (
                              <tr key={type.id} style={{ borderBottom: `1px solid ${COLORS.lightBg}` }}>
                                {/* Nom du type avec icône */}
                                <td style={{ padding: '16px' }}>
                                  <div className="d-flex align-items-center">
                                    <div className="me-3">
                                      <div 
                                        className="rounded-circle d-flex align-items-center justify-content-center fw-semibold"
                                        style={{ 
                                          width: '40px',
                                          height: '40px',
                                          fontSize: '14px',
                                          backgroundColor: COLORS.infoLight,
                                          color: COLORS.info,
                                          boxShadow: '0 2px 4px rgba(33, 150, 243, 0.1)'
                                        }}
                                        title="Type d'incident"
                                      >
                                        <i className="bi bi-exclamation-triangle"></i>
                                      </div>
                                    </div>
                                    <div>
                                      <strong className="d-block mb-1" style={{ color: COLORS.darkText }}>
                                        {type.type || type.nom || "Type sans nom"}
                                      </strong>
                                      {type.description && (
                                        <div className="small text-muted text-truncate" style={{ 
                                          maxWidth: '500px',
                                          color: '#6c757d',
                                          fontSize: '0.85rem'
                                        }}>
                                          {type.description}
                                        </div>
                                      )}
                                      
                                    </div>
                                  </div>
                                </td>
                                
                                {/* Nombre d'incidents avec badge seulement */}
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex flex-column align-items-center">
                                    {getIncidentBadge(incidentCount)}
                                    <small className="text-muted mt-1" style={{ fontSize: '0.75rem' }}>
                                      {incidentCount === 1 ? 'incident' : 'incidents'}
                                    </small>
                                  </div>
                                </td>
                                
                                {/* Actions */}
                                <td style={{ padding: '16px', textAlign: 'center' }}>
                                  <div className="d-flex gap-2 justify-content-center">
                                    <button
                                      className="btn fw-semibold"
                                      onClick={() => navigate(`/typeIncidents/edit/${type.id}`)}
                                      title="Modifier"
                                      style={{
                                        color: COLORS.primary,
                                        borderColor: COLORS.primary,
                                        backgroundColor: 'transparent',
                                        borderRadius: '6px',
                                        padding: '8px 16px',
                                        fontSize: '0.85rem',
                                        transition: 'all 0.3s ease',
                                        minWidth: '44px'
                                      }}
                                      onMouseEnter={(e) => {
                                        e.target.style.backgroundColor = COLORS.primary;
                                        e.target.style.color = COLORS.white;
                                      }}
                                      onMouseLeave={(e) => {
                                        e.target.style.backgroundColor = 'transparent';
                                        e.target.style.color = COLORS.primary;
                                      }}
                                    >
                                      <i className="bi bi-pencil"></i>
                                    </button>
                                    
                                   
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>

                    {/* Pagination */}
                    {totalPages > 1 && (
                      <div className="d-flex justify-content-between align-items-center mt-4">
                        <div className="d-flex align-items-center">
                          <span className="me-2" style={{ color: COLORS.darkText }}>Afficher :</span>
                          <select 
                            className="form-select form-select-sm w-auto"
                            value={itemsPerPage}
                            onChange={handleItemsPerPageChange}
                            style={{
                              borderColor: '#e0e0e0',
                              color: COLORS.darkText,
                              borderRadius: '6px'
                            }}
                          >
                            <option value="5">5</option>
                            <option value="10">10</option>
                            <option value="25">25</option>
                            <option value="50">50</option>
                            <option value="100">100</option>
                          </select>
                          <span className="ms-3" style={{ color: COLORS.darkText }}>
                            <span className="badge fw-semibold me-2" style={{ 
                              backgroundColor: '#E3F2FD',
                              color: COLORS.primary,
                              padding: '4px 12px',
                              borderRadius: '6px'
                            }}>
                              {filteredTypes.length} type(s)
                              {filteredTypes.length !== allTypes.length ? ' (filtrés)' : ''}
                            </span>
                          </span>
                        </div>
                        
                        <nav>
                          <ul className="pagination pagination-sm mb-0">
                            <li className={`page-item ${currentPage === 1 ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage - 1)}
                                disabled={currentPage === 1}
                                style={{
                                  color: currentPage === 1 ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-left"></i>
                              </button>
                            </li>
                            
                            {Array.from({ length: totalPages }, (_, i) => i + 1)
                              .filter(page => {
                                if (totalPages <= 7) return true;
                                return page === 1 || 
                                       page === totalPages || 
                                       (page >= currentPage - 1 && page <= currentPage + 1);
                              })
                              .map((page, index, array) => {
                                if (index > 0 && page > array[index - 1] + 1) {
                                  return (
                                    <React.Fragment key={`ellipsis-${page}`}>
                                      <li className="page-item disabled">
                                        <span className="page-link" style={{ borderColor: '#e0e0e0' }}>...</span>
                                      </li>
                                      <li className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                        <button 
                                          className="page-link fw-semibold"
                                          onClick={() => handlePageChange(page)}
                                          style={{
                                            backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                            borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                            color: currentPage === page ? COLORS.white : COLORS.primary,
                                            padding: '6px 12px',
                                            borderRadius: '6px',
                                            margin: '0 2px',
                                            transition: 'all 0.3s ease'
                                          }}
                                          onMouseEnter={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = '#E3F2FD';
                                              e.target.style.borderColor = COLORS.secondary;
                                            }
                                          }}
                                          onMouseLeave={(e) => {
                                            if (currentPage !== page) {
                                              e.target.style.backgroundColor = 'transparent';
                                              e.target.style.borderColor = '#e0e0e0';
                                            }
                                          }}
                                        >
                                          {page}
                                        </button>
                                      </li>
                                    </React.Fragment>
                                  );
                                }
                                
                                return (
                                  <li key={page} className={`page-item ${currentPage === page ? 'active' : ''}`}>
                                    <button 
                                      className="page-link fw-semibold"
                                      onClick={() => handlePageChange(page)}
                                      style={{
                                        backgroundColor: currentPage === page ? COLORS.primary : 'transparent',
                                        borderColor: currentPage === page ? COLORS.primary : '#e0e0e0',
                                        color: currentPage === page ? COLORS.white : COLORS.primary,
                                        padding: '6px 12px',
                                        borderRadius: '6px',
                                        margin: '0 2px',
                                        transition: 'all 0.3s ease'
                                      }}
                                      onMouseEnter={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = '#E3F2FD';
                                          e.target.style.borderColor = COLORS.secondary;
                                        }
                                      }}
                                      onMouseLeave={(e) => {
                                        if (currentPage !== page) {
                                          e.target.style.backgroundColor = 'transparent';
                                          e.target.style.borderColor = '#e0e0e0';
                                        }
                                      }}
                                    >
                                      {page}
                                    </button>
                                  </li>
                                );
                              })}
                            
                            <li className={`page-item ${currentPage === totalPages ? 'disabled' : ''}`}>
                              <button 
                                className="page-link fw-semibold"
                                onClick={() => handlePageChange(currentPage + 1)}
                                disabled={currentPage === totalPages}
                                style={{
                                  color: currentPage === totalPages ? '#B0BEC5' : COLORS.primary,
                                  borderColor: '#e0e0e0',
                                  padding: '6px 12px',
                                  borderRadius: '6px',
                                  margin: '0 2px'
                                }}
                              >
                                <i className="bi bi-chevron-right"></i>
                              </button>
                            </li>
                          </ul>
                        </nav>
                      </div>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        </div>
      </section>

      {/* Styles CSS additionnels */}
      <style>{`
        .pagetitle h1,
        .card-title,
        .btn,
        .page-link,
        th {
          font-family: 'Inter', sans-serif;
        }
        
        .card-body,
        .table,
        td,
        small,
        .alert {
          font-family: 'Roboto', sans-serif;
        }
        
        .btn:focus,
        .form-control:focus,
        .page-link:focus,
        .form-select:focus {
          outline: none;
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .btn,
        .badge,
        .page-link {
          transition: all 0.2s ease-in-out;
        }
        
        .btn:disabled,
        .page-link:disabled {
          cursor: not-allowed;
          opacity: 0.6;
        }
        
        .form-control:focus,
        .form-select:focus {
          border-color: ${COLORS.secondary};
          box-shadow: 0 0 0 0.2rem rgba(30, 136, 200, 0.25);
        }
        
        .table-hover tbody tr:hover {
          background-color: ${COLORS.lightBg};
          transition: background-color 0.2s ease;
        }
        
        .badge {
          font-weight: 500;
        }
        
        @media (max-width: 768px) {
          .card {
            margin: 0 -16px;
            border-radius: 0 !important;
          }
          
          .pagetitle h1 {
            font-size: 1.5rem;
          }
          
          .d-flex.justify-content-between {
            flex-direction: column;
            gap: 16px;
          }
          
          .d-flex.justify-content-between > div {
            width: 100%;
          }
          
          .btn {
            width: 100%;
            margin-bottom: 8px;
          }
          
          .table-responsive {
            border: none;
          }
          
          .table {
            font-size: 0.875rem;
          }
          
          .table th,
          .table td {
            padding: 12px 8px !important;
          }
          
          .row.g-3 {
            margin-bottom: 8px;
          }
          
          .row.g-3 > div {
            margin-bottom: 8px;
          }
        }
        
        /* Amélioration de la lisibilité des tables */
        .table th {
          text-transform: uppercase;
          letter-spacing: 0.5px;
          font-size: 0.85rem;
        }
        
        .table td {
          vertical-align: middle;
        }
        
        /* Animation pour les badges */
        .badge:hover {
          transform: translateY(-1px);
        }
        
        /* Styles pour les badges d'incidents */
        .badge {
          box-shadow: 0 2px 4px rgba(0,0,0,0.1);
        }
      `}</style>
    </Layout>
  );
};

export default TypeIncidentsList;